import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Drawer, Row, Checkbox, Card } from 'antd';
import {
  ActionWrapper,
  ActionContentWrapper,
  DesktopContentWrapper,
  MobileContentWrapper,
  TermsWrapper,
  MobileContentContainer,
} from './styles';
import { StyledParagraph, StyledTitle } from '../../components/CustomAntdComponents/Typography';
import { formatNumberAsCurrency, isSafaricomNumber } from '../../utils/helpers';
import { useBookingStore } from '../../store/booking/bookingStore';
import RoadFareBreakdown from "../../components/RoadItineraryBreakdown/RoadFareBreakdown";
import { SectionCard } from "../PassengerDetails/styles";
import RoadItinerarySummary from "../../components/RoadItineraryBreakdown/RoadItinerarySummary";
import useIsMobile from "../../hooks/useIsMobile";
import { ReactComponent as ArrowRightIcon } from "../../assets/images/caret-right.svg";
import { ActionButtonWrapper } from "../../components/FlightItinerary/styles";
import RoadBookingSummary from "../../components/RoadItineraryBreakdown/RoadBookingSummary";
import { useRoadSearchStore } from "../../store/roadSearch/roadSearchStore";
import { calculateTotalFare } from "../../components/AvailableBuses/helper";
import {
  calculateTicketTotal,
  calculateTicketTotalDiscountPrice,
  calculateTicketTotalOldPrice,
  calculateTicketNewTotal,
} from "../../components/AvailableFlights/helper";
import { StrikeThroughAmount } from "../../components/AvailableFlights/styles";
import FareRule from "../../components/FareRule/RoadFareRule";
import RoadTerms from '../../components/TermsAndConditions/RoadTerms';
import PaymentMethodSelector from "../../components/PaymentComponents/PaymentMethodSelector";
import PaymentTimerCard from "../../components/PaymentComponents/PaymentTimerCard";
import translations from "../PassengerDetails/translations";
import { useI18nContext } from "../../utils/I18n";
import RoadItineraryBreakdown from '../../components/RoadItinerary';
import RoadPriceBreakdown from '../../components/RoadItineraryBreakdown/RoadPriceBreakdown';
import { TRIPS_PAYMENT_CHANNEL } from '../../utils/constants';

import { getAPI, postAPI } from "../../utils/api";

type Props = {
  handlePayment: (method: any, mpesaPhone?: string, publicKey?: string) => void;
  loading?: boolean;
  bookingSuccess?: boolean;
  paymentMethods?: any[];
  paymentMethodsLoading?: boolean;
};

function RoadPayment({ handlePayment, loading, bookingSuccess, paymentMethods = [], paymentMethodsLoading }: Props) {
  const auth = JSON.parse(localStorage.getItem('trips_auth') || '{}');
  const isExternalPayment = auth?.ExtraData?.PaymentChannel !== TRIPS_PAYMENT_CHANNEL;
  const {
    selectedResult,
    formData: {
      numberOfAdults,
      fareRuleAccepted
    },
    selectedBus,
    bookingContact,
  } = useRoadSearchStore();

  const [accept, setAccept] = useState(Boolean(fareRuleAccepted));
  const { isMobile } = useIsMobile();
  const drawerContainerRef = useRef(null);

  const {
    state: { t },
  } = useI18nContext();

  const { bookingPayload, confirmData } = useBookingStore();
  const [selectedMethod, setSelectedMethod] = useState<any>(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [mpesaPhone, setMpesaPhone] = useState("");
  const [publicKey, setPublicKey] = useState("");

  const [phoneError, setPhoneError] = useState(false);
  const [phoneErrorText, setPhoneErrorText] = useState("");

  useEffect(() => {
    if (confirmData?.[0]?.PassengerDetails?.BillingAddress?.ContactMobileNo) {
      setMpesaPhone(confirmData[0].PassengerDetails.BillingAddress.ContactMobileNo);
    } else if (bookingContact?.phone) {
      setMpesaPhone(bookingContact.phone);
    }
  }, [confirmData, bookingContact]);

  const handlePublicKeyFetch = async (methodId: string) => {
    try {
      const res = await getAPI(`/Payment/get-key?Id=${encodeURIComponent(methodId)}`);
      if (res.status === 200 && res.data?.IsSuccessful) {
        const key = typeof res.data.Data === 'string' ? res.data.Data : (res.data.Data?.publicKey || res.data.Data?.PublicKey || res.data.Data?.Key || "");
        setPublicKey(key);
      }
    } catch (err) {
      console.error("Error fetching public API key:", err);
    }
  };

  useEffect(() => {
    if (paymentMethods && paymentMethods.length > 0) {
      const defaultMethod = paymentMethods.find(m => !m.IsMobile) || paymentMethods[0];
      setSelectedMethod(defaultMethod);
      if (defaultMethod?.HasPublicKey) {
        handlePublicKeyFetch(defaultMethod.Id);
      }
    }
  }, [paymentMethods]);

  const handlePhoneChange = (val: string) => {
    setMpesaPhone(val);
    setPhoneError(false);
    setPhoneErrorText("");
  };

  const handleMethodChange = async (methodId: string) => {
    const method = paymentMethods.find(m => m.Id === methodId);
    setSelectedMethod(method);
    setPhoneError(false);
    setPhoneErrorText("");
    setPublicKey("");

    if (method?.HasPublicKey) {
      await handlePublicKeyFetch(methodId);
    }
  };

  const onMakePayment = () => {
    if (selectedMethod?.IsMobile) {
      const cleanPhone = mpesaPhone.replace(/[\s\+]/g, "");
      if (!cleanPhone || cleanPhone.length <= 3) {
        setPhoneError(true);
        setPhoneErrorText("Phone number is compulsory");
        return;
      }
      if (!isSafaricomNumber(mpesaPhone)) {
        setPhoneError(true);
        setPhoneErrorText("Enter a valid Safaricom Number");
        return;
      }
    }
    handlePayment(selectedMethod, mpesaPhone, publicKey);
  };

  const discountAmount = confirmData
    ? calculateTicketTotalDiscountPrice(confirmData)
    : 0;

  useEffect(() => {
    setAccept(fareRuleAccepted);
  }, [fareRuleAccepted]);

  const handleChange = (e) => {
    setAccept(e.target.checked);
  };

  if (!selectedBus || selectedBus.length === 0) {
    return null;
  }

  const { totalAmount } = calculateTotalFare(selectedBus, numberOfAdults);
  const bookingAmount = bookingPayload
    ? calculateTicketTotal(bookingPayload.TicketingDetails)
    : totalAmount;

  const hasConfirmData = Boolean(confirmData && confirmData.length > 0);
  const confirmOldPrice = hasConfirmData ? calculateTicketTotalOldPrice(confirmData) : 0;
  const confirmNewPrice = hasConfirmData ? calculateTicketNewTotal(confirmData) : 0;
  const displayAmount = hasConfirmData ? confirmNewPrice : bookingAmount;

  return (
    <div ref={drawerContainerRef}>
      {isMobile ? (
        <Row>
          <Col xs={24} lg={0}>
            <MobileContentContainer>
              <MobileContentWrapper>
                <div style={{ marginBottom: "20px" }}>
                  <StyledParagraph size="default">
                    Booking summary
                  </StyledParagraph>
                </div>
                <Card style={{ marginBottom: 24, paddingBottom: 0 }}>
                  <RoadBookingSummary />
                  <div style={{ height: '1px', background: '#E5E7EB', margin: '14px -24px' }} />
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', paddingBottom: 4 }} onClick={() => setOpenDrawer(true)}>
                    <StyledParagraph size="default" style={{ margin: 0 }}>View trip summary</StyledParagraph>
                    <ArrowRightIcon />
                  </div>
                </Card>
                {!isExternalPayment && (
                  <>
                    <Card>
                      <PaymentMethodSelector
                        paymentMethods={paymentMethods}
                        selectedMethodId={selectedMethod?.Id}
                        onMethodChange={handleMethodChange}
                        phone={mpesaPhone}
                        onChangePhone={handlePhoneChange}
                        phoneError={phoneError}
                        phoneErrorText={phoneErrorText}
                        loading={paymentMethodsLoading}
                      />
                    </Card>
                    <PaymentTimerCard stop={bookingSuccess} />
                  </>
                )}
              </MobileContentWrapper>

              <ActionWrapper>
                {confirmData?.length && confirmData[0]?.IsDiscounted ? (
                  <div className="discount-container">
                    <span>{t(translations).discountApplied}</span>
                    <span className="discount-code-text">
                      {confirmData[0]?.PassengerDetails?.PromoCode}
                    </span>
                    <span className="discount-amount-off-text">
                      (
                      {formatNumberAsCurrency(
                        discountAmount,
                        selectedResult[0]?.Currency || selectedBus[0]?.Currency
                      )}{" "}
                      {t(translations).off})
                    </span>
                  </div>
                ) : null}
                <TermsWrapper>
                  <Checkbox onChange={handleChange} checked={accept}>
                    I have read and agree to the <RoadTerms inline /> and the{" "}
                    <FareRule inline />
                  </Checkbox>
                </TermsWrapper>
                <ActionContentWrapper>
                  <div>
                    <StyledParagraph>Total fare</StyledParagraph>
                    <div className="make-payment-amount-wrapper" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <StyledTitle level={3}>
                        {formatNumberAsCurrency(
                          displayAmount || 0,
                          selectedResult[0]?.Currency || selectedBus[0]?.Currency
                        )}
                        &nbsp;
                      </StyledTitle>
                      {hasConfirmData ? (
                        confirmOldPrice > confirmNewPrice ? (
                          <StrikeThroughAmount>
                            {formatNumberAsCurrency(
                              confirmOldPrice,
                              selectedResult[0]?.Currency || selectedBus[0]?.Currency
                            )}
                          </StrikeThroughAmount>
                        ) : null
                      ) : null}
                    </div>
                  </div>
                  {(paymentMethods.length > 0 || isExternalPayment) && (
                    <ActionButtonWrapper>
                      <Button
                        block
                        size="large"
                        type="primary"
                        htmlType="submit"
                        disabled={!accept}
                        loading={loading}
                        onClick={onMakePayment}
                      >
                        Make payment
                      </Button>
                    </ActionButtonWrapper>
                  )}
                </ActionContentWrapper>
              </ActionWrapper>
            </MobileContentContainer>
          </Col>
        </Row>
      ) : (
        <Row>
          <Col xs={0} lg={24}>
            <DesktopContentWrapper className="container">
              <div style={{ marginBottom: "30px" }}>
                <StyledTitle level={3}>Make payment</StyledTitle>
              </div>
              <Row>
                <Col span={13}>
                  {isExternalPayment ? (
                    <Card style={{ marginBottom: 24, padding: '24px', borderRadius: '24px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                        <StyledTitle level={4} style={{ margin: 0 }}>Price breakdown</StyledTitle>
                        <FareRule />
                      </div>
                      <RoadFareBreakdown hideBreakdown={true} hideCreditAppliedMsg={true} />
                      <div style={{ marginTop: 24 }}>
                        <Button
                          block
                          size="large"
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                          onClick={onMakePayment}
                        >
                          Make payment
                        </Button>
                      </div>
                    </Card>
                  ) : (
                    <>
                      <Card style={{ marginBottom: 24, borderRadius: '24px' }}>
                        <PaymentMethodSelector
                          paymentMethods={paymentMethods}
                          selectedMethodId={selectedMethod?.Id}
                          onMethodChange={handleMethodChange}
                          phone={mpesaPhone}
                          onChangePhone={handlePhoneChange}
                          phoneError={phoneError}
                          phoneErrorText={phoneErrorText}
                          loading={paymentMethodsLoading}
                        />

                        {paymentMethods.length > 0 && (
                          <Button
                            block
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                            onClick={onMakePayment}
                          >
                            Make payment
                          </Button>
                        )}
                      </Card>
                      <PaymentTimerCard stop={bookingSuccess} />
                    </>
                  )}
                </Col>

                <Col offset={2} span={9}>
                  <SectionCard style={{ padding: '24px', borderRadius: '24px' }}>
                    <StyledTitle level={4} style={{ marginBottom: '36px' }}>Trip summary</StyledTitle>
                    <RoadItineraryBreakdown hidePriceBreakdown />

                    <div style={{ height: '1px', background: '#E5E7EB', margin: '14px 0' }} />
                    <RoadFareBreakdown hideBreakdown={true} hideCreditAppliedMsg={true} />
                  </SectionCard>
                </Col>
              </Row>
            </DesktopContentWrapper>
          </Col>
        </Row>
      )}

      <Drawer
        title="Trip Summary"
        placement={isMobile ? "bottom" : "right"}
        onClose={() => setOpenDrawer(false)}
        open={openDrawer}
        height="100%"
        width={isMobile ? "100%" : "500px"}
        getContainer={drawerContainerRef.current as HTMLElement}
      >
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          <div style={{ padding: "0 10px 20px" }}>
            <StyledParagraph size="default">Here are your trip details</StyledParagraph>
          </div>
          {/* <div style={{ flex: 1 }}>
            <RoadItinerarySummary />
          </div>
          <div style={{ background: '#FFF8F0', padding: "24px", margin: "24px -24px -24px -24px", marginTop: "auto" }}>
            <StyledTitle level={4} style={{ marginBottom: 16 }}>Price breakdown</StyledTitle>
            <RoadFareBreakdown hideBreakdown={false} hideCreditAppliedMsg={true} />
          </div> */}



          <div style={{ flex: 1 }}>
            <RoadItineraryBreakdown hidePriceBreakdown />
          </div>
          <div style={{ background: '#FFF8F0', padding: "24px", margin: "24px -24px -24px -24px", marginTop: "auto" }}>
            <StyledTitle level={4} style={{ marginBottom: 16 }}>Price breakdown</StyledTitle>
            <RoadPriceBreakdown />
          </div>
        </div>
      </Drawer>
    </div>
  );
}

export default RoadPayment;
